import { useCallback } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';

import { ACTIVATE_PATIENT } from '@/apollo/mutations';
import { MY_CARETEAM_QUERY } from '@/apollo/queries';
import { Product, getPdtUid } from '@/utils/productHelpers';
import { logError } from '@/utils/logError';

export type ActivationOutcome =
  | { action: 'continue' }
  | { action: 'coverage'; orgSlug: string }
  | { action: 'error' };

/**
 * Activates the care team when the coverage-check screen — the only place that normally
 * does it — was skipped. Pass `knownOrgSlug` to activate even if the lookup fails.
 */
export const useEnsureActivated = (product: Product) => {
  const [getMyCareteam] = useLazyQuery(MY_CARETEAM_QUERY, { fetchPolicy: 'no-cache' });
  const [activatePatient] = useMutation(ACTIVATE_PATIENT);

  return useCallback(async (
    accessToken: string,
    knownOrgSlug?: string | null,
  ): Promise<ActivationOutcome> => {
    const pdtUid = getPdtUid(product);
    const context = { headers: { Authorization: `Bearer ${accessToken}` } };
    let orgSlug = knownOrgSlug ?? undefined;
    // Fallback if the lookup does not answer: callers pass an organization only once details exist.
    let hasDetails = Boolean(knownOrgSlug);

    try {
      const { data } = await getMyCareteam({ variables: { pdtUid }, context });
      const careTeam = data?.myCareteam;
      if (careTeam?.enrolledTime) {
        return { action: 'continue' };
      }
      if (!careTeam) {
        logError('[useEnsureActivated]', 'no care team for this user and PDT', undefined, {
          level: 'warning',
          extra: { pdtUid },
        });
      }
      orgSlug = orgSlug ?? careTeam?.userPdtData?.organizationSlug ?? undefined;
      // A date of birth means their details were captured — by caregiver or by the coverage form.
      if (careTeam) { hasDetails = Boolean(careTeam.patient?.user?.dateOfBirth); }
    } catch (err) {
      // Fall through: a caller-supplied organization still lets us activate.
      logError('[useEnsureActivated]', 'myCareteam lookup failed', err);
    }

    // Without an organization we can neither activate nor route, so leave the user as they are.
    if (!orgSlug) {
      logError('[useEnsureActivated]', 'care team has no organization slug', undefined, {
        level: 'warning',
        extra: { pdtUid },
      });
      return { action: 'continue' };
    }

    if (!hasDetails) {
      return { action: 'coverage', orgSlug };
    }

    try {
      const response = await activatePatient({
        variables: { pdtUid, organizationSlug: orgSlug },
        context,
      });
      if (!response.data?.activatePatient?.success) {
        throw new Error('activatePatient returned success=false');
      }
    } catch (err) {
      logError('[useEnsureActivated]', 'activatePatient failed', err);
      return { action: 'error' };
    }

    return { action: 'continue' };
  }, [product, getMyCareteam, activatePatient]);
};
